import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import styled from 'styled-components';
import { useLocation, useSearchParams } from 'react-router-dom';
import { athleteAPI, trainingSessionAPI, bananaCupAPI } from '../../services/api';
import { Athlete } from '../../types/mongodb';
import { getQuickDateRange } from './dateRangeUtils';
import { loadAnalyticsFilters, saveAnalyticsFilters, getPagePathFromRoute } from '../../utils/analyticsFilterStorage';
import {
  AnalyticsContainer,
  PageHeader,
  PageTitle,
  PageDescription,
  DataSelectionSection,
  SelectionGrid,
  SelectionGroup,
  GraphSection,
  GraphSectionWithPeriodSelector,
  GraphTitle,
  GraphContainer,
  EmptyState,
  PeriodSelectorTopRight,
  PeriodButtonWrapperSmall,
  PeriodButtonSmall,
  PeriodLabelSmall,
} from './styles';
import {
  DateRangeDropdown,
  AthletesDropdown,
  LocationDropdown,
  HillSizeDropdown,
  SessionTypesDropdown,
} from './FilterDropdowns';
import { getAthleteColor } from '../../utils/athleteColors';


// Custom bar shape with rounded top corners and shadow, with varying opacity
const RoundedBar = (props: any) => {
  const { fill, x, y, width, height, payload, index } = props;
  const radius = 8;
  
  // Calculate opacity based on index (each bar gets slightly less opacity)
  const barIndex = index !== undefined ? index : 0;
  const opacity = Math.max(0.6, 1.0 - barIndex * 0.03);
  
  // Create path for rectangle with only top corners rounded
  const path = `
    M ${x},${y + height}
    L ${x},${y + radius}
    Q ${x},${y} ${x + radius},${y}
    L ${x + width - radius},${y}
    Q ${x + width},${y} ${x + width},${y + radius}
    L ${x + width},${y + height}
    Z
  `;
  
  return (
    <g>
      <path
        d={path}
        fill={fill}
        fillOpacity={opacity}
        style={{
          filter: 'drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.15))',
        }}
      />
    </g>
  );
};


interface TrainingSession {
  _id: string;
  date: string;
  location: string;
  hillSize?: string;
  trainingType: string;
  jumps?: Array<{
    athleteId: string | { _id: string; firstName: string; lastName: string };
    distance?: number;
    hillSize?: string;
  }>;
  competitionJumps?: Array<{
    athleteId: string | { _id: string; firstName: string; lastName: string };
    firstRoundDistance?: number;
    secondRoundDistance?: number;
    hillSize?: string;
  }>;
  groundTrainings?: Array<any>;
  crossCountryTrainings?: Array<any>;
}

interface DistanceAnalyticsProps {
  isJumperMode?: boolean;
}

const DistanceAnalytics: React.FC<DistanceAnalyticsProps> = ({ isJumperMode = false }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const pagePath = getPagePathFromRoute(location.pathname, searchParams);
  
  // Load saved filters on mount
  const savedFilters = loadAnalyticsFilters(pagePath);
  
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [bananaCups, setBananaCups] = useState<any[]>([]);
  const [selectedAthletes, setSelectedAthletes] = useState<string[]>(savedFilters?.selectedAthletes || []);
  const [startDate, setStartDate] = useState<string>(savedFilters?.startDate || '');
  const [endDate, setEndDate] = useState<string>(savedFilters?.endDate || '');
  const [selectedLocation, setSelectedLocation] = useState<string>(savedFilters?.selectedLocation || '');
  const [selectedHillSize, setSelectedHillSize] = useState<string>(savedFilters?.selectedHillSize || '');
  const [selectedSessionTypes, setSelectedSessionTypes] = useState<string[]>(savedFilters?.selectedSessionTypes || []);
  const [selectedPeriod, setSelectedPeriod] = useState<'daily' | 'weekly' | 'monthly'>(savedFilters?.selectedPeriod || 'daily');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [locations, setLocations] = useState<string[]>([]);
  const [hillSizes, setHillSizes] = useState<string[]>([]);
  const [jumperAthleteId, setJumperAthleteId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(typeof window !== 'undefined' && window.innerWidth <= 768);
  const isInitialMount = useRef(true);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Data states for graphs - daily, weekly, monthly
  const [avgDistanceLineDataDaily, setAvgDistanceLineDataDaily] = useState<any[]>([]);
  const [avgDistanceLineDataWeekly, setAvgDistanceLineDataWeekly] = useState<any[]>([]);
  const [avgDistanceLineDataMonthly, setAvgDistanceLineDataMonthly] = useState<any[]>([]);
  const [topDistanceLineDataDaily, setTopDistanceLineDataDaily] = useState<any[]>([]);
  const [topDistanceLineDataWeekly, setTopDistanceLineDataWeekly] = useState<any[]>([]);
  const [topDistanceLineDataMonthly, setTopDistanceLineDataMonthly] = useState<any[]>([]);
  const [worstDistanceLineDataDaily, setWorstDistanceLineDataDaily] = useState<any[]>([]);
  const [worstDistanceLineDataWeekly, setWorstDistanceLineDataWeekly] = useState<any[]>([]);
  const [worstDistanceLineDataMonthly, setWorstDistanceLineDataMonthly] = useState<any[]>([]);
  const [combinedDistanceLineData, setCombinedDistanceLineData] = useState<any[]>([]);
  const [avgDistanceBarData, setAvgDistanceBarData] = useState<any[]>([]);
  const [topDistanceBarData, setTopDistanceBarData] = useState<any[]>([]);
  const [avgDistanceByLocation, setAvgDistanceByLocation] = useState<any[]>([]);
  const [bestDistanceByLocation, setBestDistanceByLocation] = useState<any[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // If jumper mode, get the logged-in jumper's athlete ID first
        if (isJumperMode) {
          try {
            const myAthlete = await athleteAPI.getMyAthlete();
            const athleteId = myAthlete._id.toString();
            setJumperAthleteId(athleteId);
            setSelectedAthletes([athleteId]);
          } catch (error) {
            console.error('Error loading jumper athlete:', error);
          }
        }
        
        const [athletesData, sessionsResponse, bananaCupsData] = await Promise.all([
          athleteAPI.getAll(),
          trainingSessionAPI.getAll(undefined, undefined, true), // Load all sessions for analytics
          bananaCupAPI.getAll(),
        ]);

        // Handle response - when all=true, returns array directly, otherwise paginated object
        const sessionsData = Array.isArray(sessionsResponse) ? sessionsResponse : (sessionsResponse.sessions || []);

        setAthletes(athletesData);
        setSessions(sessionsData);
        setBananaCups(bananaCupsData || []);

        // Extract unique locations and hill sizes
        const uniqueLocations = Array.from(new Set(sessionsData.map((s: TrainingSession) => s.location).filter(Boolean)));
        const uniqueHillSizes = Array.from(new Set(
          sessionsData.flatMap((s: TrainingSession) => [
            s.hillSize,
            ...(s.jumps?.map(j => (j as any).hillSize) || []),
            ...(s.competitionJumps?.map(j => (j as any).hillSize) || []),
          ].filter(Boolean))
        ));

        setLocations(uniqueLocations as string[]);
        setHillSizes(uniqueHillSizes as string[]);

        // Set default date range (last 3 months) only if no saved filters exist
        if (!savedFilters || (!savedFilters.startDate && !savedFilters.endDate)) {
          const today = new Date();
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(today.getMonth() - 3);
          setEndDate(today.toISOString().split('T')[0]);
          setStartDate(threeMonthsAgo.toISOString().split('T')[0]);
        }
        
        // Set default session types if none selected
        if (!savedFilters?.selectedSessionTypes || savedFilters.selectedSessionTypes.length === 0) {
          setSelectedSessionTypes(['ski_jumping', 'competition']);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isJumperMode]);

  // Save filters whenever they change (skip on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    saveAnalyticsFilters(pagePath, {
      selectedAthletes,
      startDate,
      endDate,
      selectedLocation,
      selectedHillSize,
      selectedSessionTypes,
      selectedPeriod,
    });
  }, [pagePath, selectedAthletes, startDate, endDate, selectedLocation, selectedHillSize, selectedSessionTypes, selectedPeriod]);

  // Helper function to format date for input
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    if (dateString.includes('-') && dateString.length === 10) {
      return dateString;
    }
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateString;
  };

  // Helper function to check if date is in range
  const isDateInRange = (dateString: string, start: string, end: string): boolean => {
    if (!start || !end) return true;
    return dateString >= start && dateString <= end;
  };

  // Process distance data
  useEffect(() => {
    if (!sessions.length || !startDate || !endDate || selectedSessionTypes.length === 0 || selectedAthletes.length === 0) {
      setAvgDistanceLineDataDaily([]);
      setAvgDistanceLineDataWeekly([]);
      setAvgDistanceLineDataMonthly([]);
      setTopDistanceLineDataDaily([]);
      setTopDistanceLineDataWeekly([]);
      setTopDistanceLineDataMonthly([]);
      setWorstDistanceLineDataDaily([]);
      setWorstDistanceLineDataWeekly([]);
      setWorstDistanceLineDataMonthly([]);
      setCombinedDistanceLineData([]);
      setAvgDistanceBarData([]);
      setTopDistanceBarData([]);
      setAvgDistanceByLocation([]);
      return;
    }

    // Helper function to check if a session has been recorded (has actual data)
    const isRecordedSession = (session: TrainingSession): boolean => {
      const hasJumps = !!(session.jumps && session.jumps.length > 0);
      const hasCompetitionJumps = !!(session.competitionJumps && session.competitionJumps.length > 0);
      const hasGroundTrainings = !!(session.groundTrainings && session.groundTrainings.length > 0);
      const hasCrossCountryTrainings = !!(session.crossCountryTrainings && session.crossCountryTrainings.length > 0);
      return hasJumps || hasCompetitionJumps || hasGroundTrainings || hasCrossCountryTrainings;
    };

    // Filter sessions by date range, location, hill size, and session types
    // Also exclude pre-planned sessions (sessions with no actual data)
    const filteredSessions = sessions.filter(session => {
      if (!isDateInRange(session.date, startDate, endDate)) return false;
      if (selectedLocation && session.location !== selectedLocation) return false;
      // Filter by selected session types
      if (!selectedSessionTypes.includes(session.trainingType)) return false;
      // Exclude pre-planned sessions (only include sessions with actual data)
      if (!isRecordedSession(session)) return false;
      return true;
    });

    // Get athletes to process (only selected athletes)
    const athletesToProcess = athletes.filter(a => selectedAthletes.includes(a._id));

    // Collect all distances per athlete and date
    const athleteDistanceMap = new Map<string, Map<string, number[]>>(); // athleteId -> date -> distances[]
    const locationDistanceMap = new Map<string, number[]>(); // location -> distances[]

    filteredSessions.forEach(session => {
      // Process regular jumps
      if (session.jumps) {
        session.jumps.forEach(jump => {
          const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
          const athleteId = String(athleteIdRaw);
          
          // Check if athlete is selected
          if (!selectedAthletes.includes(athleteId)) return;
          
          // Check hill size if selected
          const jumpHillSize = jump.hillSize || session.hillSize;
          if (selectedHillSize && jumpHillSize !== selectedHillSize) return;

          if (jump.distance) {
            // Add to athlete distance map
            if (!athleteDistanceMap.has(athleteId)) {
              athleteDistanceMap.set(athleteId, new Map());
            }
            const dateMap = athleteDistanceMap.get(athleteId)!;
            if (!dateMap.has(session.date)) {
              dateMap.set(session.date, []);
            }
            dateMap.get(session.date)!.push(jump.distance);

            // Add to location distance map
            if (!locationDistanceMap.has(session.location)) {
              locationDistanceMap.set(session.location, []);
            }
            locationDistanceMap.get(session.location)!.push(jump.distance);
          }
        });
      }

      // Process competition jumps
      if (session.competitionJumps) {
        session.competitionJumps.forEach(jump => {
          const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
          const athleteId = String(athleteIdRaw);
          
          if (!selectedAthletes.includes(athleteId)) return;
          
          const jumpHillSize = jump.hillSize || session.hillSize;
          if (selectedHillSize && jumpHillSize !== selectedHillSize) return;

          if (!athleteDistanceMap.has(athleteId)) {
            athleteDistanceMap.set(athleteId, new Map());
          }
          const dateMap = athleteDistanceMap.get(athleteId)!;
          if (!dateMap.has(session.date)) {
            dateMap.set(session.date, []);
          }

          // Process all rounds (trial round, competition rounds, banana cup can have up to 5 rounds)
          const rounds = [
            (jump as any).trialRoundDistance,
            jump.firstRoundDistance,
            jump.secondRoundDistance,
            (jump as any).thirdRoundDistance,
            (jump as any).fourthRoundDistance,
            (jump as any).fifthRoundDistance,
          ].filter(Boolean) as number[];

          rounds.forEach(distance => {
            dateMap.get(session.date)!.push(distance);
            if (!locationDistanceMap.has(session.location)) {
              locationDistanceMap.set(session.location, []);
            }
            locationDistanceMap.get(session.location)!.push(distance);
          });
        });
      }
    });

    // Process banana cup jumps (only if banana_cup is selected)
    if (selectedSessionTypes.includes('banana_cup')) {
      bananaCups.forEach(bananaCup => {
        if (!bananaCup.competitions || !Array.isArray(bananaCup.competitions)) return;
        
        bananaCup.competitions.forEach((competition: any) => {
          if (!competition.isCompleted || !competition.jumps || !Array.isArray(competition.jumps)) return;
          
          // Check if competition date is in range
          if (!isDateInRange(competition.date, startDate, endDate)) return;
          if (selectedLocation && competition.location !== selectedLocation) return;
          
          competition.jumps.forEach((jump: any) => {
          const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
          const athleteId = String(athleteIdRaw);
          
          if (selectedAthletes.length > 0 && !selectedAthletes.includes(athleteId)) return;
          
          const jumpHillSize = jump.hillSize || competition.hillSize;
          if (selectedHillSize && jumpHillSize !== selectedHillSize) return;

          if (!athleteDistanceMap.has(athleteId)) {
            athleteDistanceMap.set(athleteId, new Map());
          }
          const dateMap = athleteDistanceMap.get(athleteId)!;
          if (!dateMap.has(competition.date)) {
            dateMap.set(competition.date, []);
          }

          // Process all rounds (banana cup can have up to 5 rounds)
          const rounds = [
            jump.firstRoundDistance,
            jump.secondRoundDistance,
            jump.thirdRoundDistance,
            jump.fourthRoundDistance,
            jump.fifthRoundDistance,
          ].filter(Boolean) as number[];

          rounds.forEach(distance => {
            dateMap.get(competition.date)!.push(distance);
            if (!locationDistanceMap.has(competition.location)) {
              locationDistanceMap.set(competition.location, []);
            }
            locationDistanceMap.get(competition.location)!.push(distance);
          });
          });
        });
      });
    }

    // Generate date ranges
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Daily data
    const avgLineDataDaily: any[] = [];
    const topLineDataDaily: any[] = [];
    const worstLineDataDaily: any[] = [];
    const current = new Date(start);
    while (current <= end) {
      const dateStr = current.toISOString().split('T')[0];
      const avgPoint: any = { date: dateStr };
      const topPoint: any = { date: dateStr };
      const worstPoint: any = { date: dateStr };

      athletesToProcess.forEach(athlete => {
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        const athleteIdStr = String(athlete._id);
        const dateMap = athleteDistanceMap.get(athleteIdStr);
        
        if (dateMap && dateMap.has(dateStr)) {
          const distances = dateMap.get(dateStr)!;
          const avg = distances.reduce((a, b) => a + b, 0) / distances.length;
          const top = Math.max(...distances);
          const worst = Math.min(...distances);

          avgPoint[athleteName] = Math.round(avg * 10) / 10;
          topPoint[athleteName] = top;
          worstPoint[athleteName] = worst;
        } else {
          avgPoint[athleteName] = null;
          topPoint[athleteName] = null;
          worstPoint[athleteName] = null;
        }
      });

      avgLineDataDaily.push(avgPoint);
      topLineDataDaily.push(topPoint);
      worstLineDataDaily.push(worstPoint);
      current.setDate(current.getDate() + 1);
    }

    // Weekly data
    const avgLineDataWeekly: any[] = [];
    const topLineDataWeekly: any[] = [];
    const worstLineDataWeekly: any[] = [];
    const weekStart = new Date(start);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1); // Start of week (Monday)
    
    while (weekStart <= end) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const weekKey = weekStart.toISOString().split('T')[0];
      const avgPoint: any = { week: weekKey };
      const topPoint: any = { week: weekKey };
      const worstPoint: any = { week: weekKey };

      athletesToProcess.forEach(athlete => {
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        const athleteIdStr = String(athlete._id);
        const dateMap = athleteDistanceMap.get(athleteIdStr);
        
        const weekDistances: number[] = [];
        const checkDate = new Date(weekStart);
        while (checkDate <= weekEnd && checkDate <= end) {
          const dateStr = checkDate.toISOString().split('T')[0];
          if (dateMap && dateMap.has(dateStr)) {
            weekDistances.push(...dateMap.get(dateStr)!);
          }
          checkDate.setDate(checkDate.getDate() + 1);
        }
        
        if (weekDistances.length > 0) {
          const avg = weekDistances.reduce((a, b) => a + b, 0) / weekDistances.length;
          const top = Math.max(...weekDistances);
          const worst = Math.min(...weekDistances);
          avgPoint[athleteName] = Math.round(avg * 10) / 10;
          topPoint[athleteName] = top;
          worstPoint[athleteName] = worst;
        } else {
          avgPoint[athleteName] = null;
          topPoint[athleteName] = null;
          worstPoint[athleteName] = null;
        }
      });

      avgLineDataWeekly.push(avgPoint);
      topLineDataWeekly.push(topPoint);
      worstLineDataWeekly.push(worstPoint);
      weekStart.setDate(weekStart.getDate() + 7);
    }

    // Monthly data
    const avgLineDataMonthly: any[] = [];
    const topLineDataMonthly: any[] = [];
    const worstLineDataMonthly: any[] = [];
    const monthStart = new Date(start.getFullYear(), start.getMonth(), 1);
    
    while (monthStart <= end) {
      const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
      const monthKey = `${monthStart.getFullYear()}-${String(monthStart.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      const avgPoint: any = { month: monthLabel };
      const topPoint: any = { month: monthLabel };
      const worstPoint: any = { month: monthLabel };

      athletesToProcess.forEach(athlete => {
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        const athleteIdStr = String(athlete._id);
        const dateMap = athleteDistanceMap.get(athleteIdStr);
        
        const monthDistances: number[] = [];
        const checkDate = new Date(monthStart);
        while (checkDate <= monthEnd && checkDate <= end) {
          const dateStr = checkDate.toISOString().split('T')[0];
          if (dateMap && dateMap.has(dateStr)) {
            monthDistances.push(...dateMap.get(dateStr)!);
          }
          checkDate.setDate(checkDate.getDate() + 1);
        }
        
        if (monthDistances.length > 0) {
          const avg = monthDistances.reduce((a, b) => a + b, 0) / monthDistances.length;
          const top = Math.max(...monthDistances);
          const worst = Math.min(...monthDistances);
          avgPoint[athleteName] = Math.round(avg * 10) / 10;
          topPoint[athleteName] = top;
          worstPoint[athleteName] = worst;
        } else {
          avgPoint[athleteName] = null;
          topPoint[athleteName] = null;
          worstPoint[athleteName] = null;
        }
      });

      avgLineDataMonthly.push(avgPoint);
      topLineDataMonthly.push(topPoint);
      worstLineDataMonthly.push(worstPoint);
      monthStart.setMonth(monthStart.getMonth() + 1);
    }

    setAvgDistanceLineDataDaily(avgLineDataDaily);
    setAvgDistanceLineDataWeekly(avgLineDataWeekly);
    setAvgDistanceLineDataMonthly(avgLineDataMonthly);
    setTopDistanceLineDataDaily(topLineDataDaily);
    setTopDistanceLineDataWeekly(topLineDataWeekly);
    setTopDistanceLineDataMonthly(topLineDataMonthly);
    setWorstDistanceLineDataDaily(worstLineDataDaily);
    setWorstDistanceLineDataWeekly(worstLineDataWeekly);
    setWorstDistanceLineDataMonthly(worstLineDataMonthly);

    // Combined data (still using daily for now)
    const combinedLineData: any[] = [];
    const currentForCombined = new Date(start);
    while (currentForCombined <= end) {
      const dateStr = currentForCombined.toISOString().split('T')[0];
      const combinedPoint: any = { date: dateStr };

      athletesToProcess.forEach(athlete => {
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        const athleteIdStr = String(athlete._id);
        const dateMap = athleteDistanceMap.get(athleteIdStr);
        
        if (dateMap && dateMap.has(dateStr)) {
          const distances = dateMap.get(dateStr)!;
          const avg = distances.reduce((a, b) => a + b, 0) / distances.length;
          const top = Math.max(...distances);
          const worst = Math.min(...distances);
          combinedPoint[`${athleteName} (Avg)`] = Math.round(avg * 10) / 10;
          combinedPoint[`${athleteName} (Top)`] = top;
          combinedPoint[`${athleteName} (Worst)`] = worst;
        } else {
          combinedPoint[`${athleteName} (Avg)`] = null;
          combinedPoint[`${athleteName} (Top)`] = null;
          combinedPoint[`${athleteName} (Worst)`] = null;
        }
      });

      combinedLineData.push(combinedPoint);
      currentForCombined.setDate(currentForCombined.getDate() + 1);
    }
    setCombinedDistanceLineData(combinedLineData);

    // Process data for bar charts (aggregated per athlete)
    const avgBarData: any[] = [];
    const topBarData: any[] = [];

    athletesToProcess.forEach(athlete => {
      const athleteName = `${athlete.firstName} ${athlete.lastName}`;
      const athleteIdStr = String(athlete._id);
      const dateMap = athleteDistanceMap.get(athleteIdStr);
      
      if (dateMap) {
        const allDistances: number[] = [];
        dateMap.forEach(distances => {
          allDistances.push(...distances);
        });

        if (allDistances.length > 0) {
          const avg = allDistances.reduce((a, b) => a + b, 0) / allDistances.length;
          const top = Math.max(...allDistances);

          avgBarData.push({
            athlete: athleteName,
            distance: Math.round(avg * 10) / 10,
          });

          topBarData.push({
            athlete: athleteName,
            distance: top,
          });
        }
      }
    });

    avgBarData.sort((a, b) => b.distance - a.distance);
    topBarData.sort((a, b) => b.distance - a.distance);

    setAvgDistanceBarData(avgBarData);
    setTopDistanceBarData(topBarData);

    // Process average distances by location
    const locationAvgData = Array.from(locationDistanceMap.entries())
      .map(([location, distances]) => ({
        location,
        averageDistance: distances.length > 0 
          ? Math.round((distances.reduce((a, b) => a + b, 0) / distances.length) * 10) / 10 
          : 0,
      }))
      .sort((a, b) => b.averageDistance - a.averageDistance);

    setAvgDistanceByLocation(locationAvgData);

    // Process best distances by location
    const locationBestData = Array.from(locationDistanceMap.entries())
      .map(([location, distances]) => ({
        location,
        bestDistance: distances.length > 0 
          ? Math.max(...distances)
          : 0,
      }))
      .sort((a, b) => b.bestDistance - a.bestDistance);

    setBestDistanceByLocation(locationBestData);
  }, [sessions, startDate, endDate, selectedAthletes, selectedLocation, selectedHillSize, athletes, bananaCups, selectedSessionTypes, isJumperMode]);

  const handleAthleteToggle = (athleteId: string) => {
    setSelectedAthletes(prev =>
      prev.includes(athleteId)
        ? prev.filter(id => id !== athleteId)
        : [...prev, athleteId]
    );
  };

  const handleUpdateDate = () => {
    // Data will be re-processed automatically via useEffect
  };

  const handleQuickDateRange = (range: string) => {
    const { startDate: newStartDate, endDate: newEndDate } = getQuickDateRange(range);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleSessionTypeToggle = (sessionType: string) => {
    setSelectedSessionTypes(prev =>
      prev.includes(sessionType)
        ? prev.filter(type => type !== sessionType)
        : [...prev, sessionType]
    );
  };

  const handleAthletesSave = (selected: string[]) => {
    setSelectedAthletes(selected);
  };

  const handleSessionTypesSave = (types: string[]) => {
    setSelectedSessionTypes(types);
  };


  // Helper function to calculate Y-axis domain (min - 10, auto max)
  const calculateYAxisDomain = (data: any[], dataKeys: string[]): [number, string] => {
    let minValue = Infinity;
    
    data.forEach(point => {
      dataKeys.forEach(key => {
        const value = point[key];
        if (value !== null && value !== undefined && typeof value === 'number' && value < minValue) {
          minValue = value;
        }
      });
    });
    
    if (minValue === Infinity) {
      return [0, 'auto'];
    }
    
    return [Math.max(0, minValue - 10), 'auto'];
  };

  if (isLoading) {
    return (
      <AnalyticsContainer>
        <EmptyState>Loading distance data...</EmptyState>
      </AnalyticsContainer>
    );
  }

  return (
    <AnalyticsContainer>
      <PageHeader>
        <PageTitle>Distance Analytics</PageTitle>
        <PageDescription>
          Track and analyze jump distances over time, identify trends, and compare performance across different conditions.
        </PageDescription>
      </PageHeader>

      {/* Data Selection Section */}
      <DataSelectionSection>
        <SelectionGrid>
          <SelectionGroup>
            <DateRangeDropdown
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onQuickDateRange={handleQuickDateRange}
              onUpdate={handleUpdateDate}
              formatDateForInput={formatDateForInput}
            />
          </SelectionGroup>

          {!isJumperMode && (
            <SelectionGroup>
              <AthletesDropdown
                athletes={athletes}
                selectedAthletes={selectedAthletes}
                onAthleteToggle={handleAthleteToggle}
                onSave={handleAthletesSave}
              />
            </SelectionGroup>
          )}

          <SelectionGroup>
            <SessionTypesDropdown
              selectedSessionTypes={selectedSessionTypes}
              onSessionTypeToggle={handleSessionTypeToggle}
              onSave={handleSessionTypesSave}
            />
          </SelectionGroup>

          <SelectionGroup>
            <LocationDropdown
              locations={locations}
              selectedLocation={selectedLocation}
              onLocationChange={setSelectedLocation}
            />
          </SelectionGroup>

          <SelectionGroup>
            <HillSizeDropdown
              hillSizes={hillSizes}
              selectedHillSize={selectedHillSize}
              onHillSizeChange={setSelectedHillSize}
            />
          </SelectionGroup>
        </SelectionGrid>
      </DataSelectionSection>

      {/* Graphs Section - Professional Layout */}
      
      {/* Average Distance Line Chart - Full Width */}
      <GraphSectionWithPeriodSelector>
        <GraphTitle>Average Distance</GraphTitle>
        {(() => {
          const hasDailyData = avgDistanceLineDataDaily.length > 0 && avgDistanceLineDataDaily.some(d => Object.keys(d).some(k => k !== 'date' && d[k] !== null));
          const hasWeeklyData = avgDistanceLineDataWeekly.length > 0 && avgDistanceLineDataWeekly.some(d => Object.keys(d).some(k => k !== 'week' && d[k] !== null));
          const hasMonthlyData = avgDistanceLineDataMonthly.length > 0 && avgDistanceLineDataMonthly.some(d => Object.keys(d).some(k => k !== 'month' && d[k] !== null));
          const hasAnyData = hasDailyData || hasWeeklyData || hasMonthlyData;
          
          return hasAnyData ? (
            <PeriodSelectorTopRight>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedPeriod === 'daily'}
                  onClick={() => setSelectedPeriod('daily')}
                  title="Daily"
                >
                  D
                </PeriodButtonSmall>
                <PeriodLabelSmall>Daily</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedPeriod === 'weekly'}
                  onClick={() => setSelectedPeriod('weekly')}
                  title="Weekly"
                >
                  W
                </PeriodButtonSmall>
                <PeriodLabelSmall>Weekly</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedPeriod === 'monthly'}
                  onClick={() => setSelectedPeriod('monthly')}
                  title="Monthly"
                >
                  M
                </PeriodButtonSmall>
                <PeriodLabelSmall>Monthly</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
            </PeriodSelectorTopRight>
          ) : null;
        })()}
        {startDate && endDate && selectedSessionTypes.length > 0 && selectedAthletes.length > 0 ? (
          (() => {
            const currentData = selectedPeriod === 'daily' ? avgDistanceLineDataDaily : selectedPeriod === 'weekly' ? avgDistanceLineDataWeekly : avgDistanceLineDataMonthly;
            const dataKey = selectedPeriod === 'daily' ? 'date' : selectedPeriod === 'weekly' ? 'week' : 'month';
            const hasData = currentData.length > 0 && currentData.some(d => Object.keys(d).some(k => k !== dataKey && d[k] !== null));
            
            return hasData ? (
              <GraphContainer>
                <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                  <LineChart data={currentData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={dataKey} 
                      style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                      tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                      tickFormatter={(value) => {
                        if (selectedPeriod === 'monthly') return value;
                        try {
                          const date = new Date(value);
                          if (isNaN(date.getTime())) return value;
                          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        } catch {
                          return value;
                        }
                      }}
                    />
                    <YAxis 
                      domain={calculateYAxisDomain(
                      currentData,
                      athletes
                        .filter(a => selectedAthletes.includes(a._id))
                        .map(a => `${a.firstName} ${a.lastName}`)
                    )} 
                      style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                      tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                    />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : '0.75rem' }} iconSize={isMobile ? 8 : 12} />
                    {athletes
                      .filter(a => selectedAthletes.length === 0 || selectedAthletes.includes(a._id))
                      .map((athlete, index) => {
                        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
                        return (
                          <Line
                            key={athlete._id}
                            type="monotone"
                            dataKey={athleteName}
                            stroke={getAthleteColor(athlete, index)}
                            strokeWidth={2}
                            name={athleteName}
                            connectNulls={true}
                          />
                        );
                      })}
                  </LineChart>
                </ResponsiveContainer>
              </GraphContainer>
            ) : (
              <EmptyState>No average distance data available for the selected period</EmptyState>
            );
          })()
        ) : (
          <EmptyState>No average distance data available</EmptyState>
        )}
      </GraphSectionWithPeriodSelector>

      {/* Top Distance Line Chart - Full Width */}
      <GraphSectionWithPeriodSelector>
        <GraphTitle>Top Distance</GraphTitle>
        {(() => {
          const hasDailyData = topDistanceLineDataDaily.length > 0 && topDistanceLineDataDaily.some(d => Object.keys(d).some(k => k !== 'date' && d[k] !== null));
          const hasWeeklyData = topDistanceLineDataWeekly.length > 0 && topDistanceLineDataWeekly.some(d => Object.keys(d).some(k => k !== 'week' && d[k] !== null));
          const hasMonthlyData = topDistanceLineDataMonthly.length > 0 && topDistanceLineDataMonthly.some(d => Object.keys(d).some(k => k !== 'month' && d[k] !== null));
          const hasAnyData = hasDailyData || hasWeeklyData || hasMonthlyData;
          
          return hasAnyData ? (
            <PeriodSelectorTopRight>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedPeriod === 'daily'}
                  onClick={() => setSelectedPeriod('daily')}
                  title="Daily"
                >
                  D
                </PeriodButtonSmall>
                <PeriodLabelSmall>Daily</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedPeriod === 'weekly'}
                  onClick={() => setSelectedPeriod('weekly')}
                  title="Weekly"
                >
                  W
                </PeriodButtonSmall>
                <PeriodLabelSmall>Weekly</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedPeriod === 'monthly'}
                  onClick={() => setSelectedPeriod('monthly')}
                  title="Monthly"
                >
                  M
                </PeriodButtonSmall>
                <PeriodLabelSmall>Monthly</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
            </PeriodSelectorTopRight>
          ) : null;
        })()}
        {startDate && endDate && selectedSessionTypes.length > 0 && selectedAthletes.length > 0 ? (
          (() => {
            const currentData = selectedPeriod === 'daily' ? topDistanceLineDataDaily : selectedPeriod === 'weekly' ? topDistanceLineDataWeekly : topDistanceLineDataMonthly;
            const dataKey = selectedPeriod === 'daily' ? 'date' : selectedPeriod === 'weekly' ? 'week' : 'month';
            const hasData = currentData.length > 0 && currentData.some(d => Object.keys(d).some(k => k !== dataKey && d[k] !== null));
            
            return hasData ? (
              <GraphContainer>
                <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                  <LineChart data={currentData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={dataKey} 
                      style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                      tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                      tickFormatter={(value) => {
                        if (selectedPeriod === 'monthly') return value;
                        try {
                          const date = new Date(value);
                          if (isNaN(date.getTime())) return value;
                          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        } catch {
                          return value;
                        }
                      }}
                    />
                    <YAxis 
                      domain={calculateYAxisDomain(
                      currentData,
                      athletes
                        .filter(a => selectedAthletes.includes(a._id))
                        .map(a => `${a.firstName} ${a.lastName}`)
                    )} 
                      style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                      tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                    />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : '0.75rem' }} iconSize={isMobile ? 8 : 12} />
                    {athletes
                      .filter(a => selectedAthletes.length === 0 || selectedAthletes.includes(a._id))
                      .map((athlete, index) => {
                        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
                        return (
                          <Line
                            key={athlete._id}
                            type="monotone"
                            dataKey={athleteName}
                            stroke={getAthleteColor(athlete, index)}
                            strokeWidth={2}
                            name={athleteName}
                            connectNulls={true}
                          />
                        );
                      })}
                  </LineChart>
                </ResponsiveContainer>
              </GraphContainer>
            ) : (
              <EmptyState>No top distance data available for the selected period</EmptyState>
            );
          })()
        ) : (
          <EmptyState>No top distance data available</EmptyState>
        )}
      </GraphSectionWithPeriodSelector>

      {/* Worst Distance Line Chart - Full Width */}
      <GraphSectionWithPeriodSelector>
        <GraphTitle>Worst Distance</GraphTitle>
        {(() => {
          const hasDailyData = worstDistanceLineDataDaily.length > 0 && worstDistanceLineDataDaily.some(d => Object.keys(d).some(k => k !== 'date' && d[k] !== null));
          const hasWeeklyData = worstDistanceLineDataWeekly.length > 0 && worstDistanceLineDataWeekly.some(d => Object.keys(d).some(k => k !== 'week' && d[k] !== null));
          const hasMonthlyData = worstDistanceLineDataMonthly.length > 0 && worstDistanceLineDataMonthly.some(d => Object.keys(d).some(k => k !== 'month' && d[k] !== null));
          const hasAnyData = hasDailyData || hasWeeklyData || hasMonthlyData;
          
          return hasAnyData ? (
            <PeriodSelectorTopRight>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedPeriod === 'daily'}
                  onClick={() => setSelectedPeriod('daily')}
                  title="Daily"
                >
                  D
                </PeriodButtonSmall>
                <PeriodLabelSmall>Daily</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedPeriod === 'weekly'}
                  onClick={() => setSelectedPeriod('weekly')}
                  title="Weekly"
                >
                  W
                </PeriodButtonSmall>
                <PeriodLabelSmall>Weekly</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedPeriod === 'monthly'}
                  onClick={() => setSelectedPeriod('monthly')}
                  title="Monthly"
                >
                  M
                </PeriodButtonSmall>
                <PeriodLabelSmall>Monthly</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
            </PeriodSelectorTopRight>
          ) : null;
        })()}
        {startDate && endDate && selectedSessionTypes.length > 0 && selectedAthletes.length > 0 ? (
          (() => {
            const currentData = selectedPeriod === 'daily' ? worstDistanceLineDataDaily : selectedPeriod === 'weekly' ? worstDistanceLineDataWeekly : worstDistanceLineDataMonthly;
            const dataKey = selectedPeriod === 'daily' ? 'date' : selectedPeriod === 'weekly' ? 'week' : 'month';
            const hasData = currentData.length > 0 && currentData.some(d => Object.keys(d).some(k => k !== dataKey && d[k] !== null));
            
            return hasData ? (
              <GraphContainer>
                <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                  <LineChart data={currentData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={dataKey} 
                      style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                      tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                      tickFormatter={(value) => {
                        if (selectedPeriod === 'monthly') return value;
                        try {
                          const date = new Date(value);
                          if (isNaN(date.getTime())) return value;
                          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        } catch {
                          return value;
                        }
                      }}
                    />
                    <YAxis 
                      domain={calculateYAxisDomain(
                      currentData,
                      athletes
                        .filter(a => selectedAthletes.includes(a._id))
                        .map(a => `${a.firstName} ${a.lastName}`)
                    )} 
                      style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                      tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                    />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : '0.75rem' }} iconSize={isMobile ? 8 : 12} />
                    {athletes
                      .filter(a => selectedAthletes.length === 0 || selectedAthletes.includes(a._id))
                      .map((athlete, index) => {
                        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
                        return (
                          <Line
                            key={athlete._id}
                            type="monotone"
                            dataKey={athleteName}
                            stroke={getAthleteColor(athlete, index)}
                            strokeWidth={2}
                            name={athleteName}
                            connectNulls={true}
                          />
                        );
                      })}
                  </LineChart>
                </ResponsiveContainer>
              </GraphContainer>
            ) : (
              <EmptyState>No worst distance data available for the selected period</EmptyState>
            );
          })()
        ) : (
          <EmptyState>No worst distance data available</EmptyState>
        )}
      </GraphSectionWithPeriodSelector>

      {/* Combined Distance Line Chart - Full Width */}
      <GraphSection>
        <GraphTitle>Combined: Top, Average, and Worst Distances</GraphTitle>
        {startDate && endDate && selectedSessionTypes.length > 0 && combinedDistanceLineData.length > 0 ? (
          <GraphContainer>
            <ResponsiveContainer width="100%" height="100%" minHeight={260}>
              <LineChart data={combinedDistanceLineData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                  tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                  tickFormatter={(value) => {
                    try {
                      const date = new Date(value);
                      if (isNaN(date.getTime())) return value;
                      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    } catch {
                      return value;
                    }
                  }}
                />
                <YAxis 
                  domain={calculateYAxisDomain(
                  combinedDistanceLineData,
                  athletes
                    .filter(a => selectedAthletes.includes(a._id))
                    .flatMap(a => {
                      const name = `${a.firstName} ${a.lastName}`;
                      return [`${name} (Avg)`, `${name} (Top)`, `${name} (Worst)`];
                    })
                )} 
                  style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                  tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                />
                <Tooltip />
                <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : '0.75rem' }} iconSize={isMobile ? 8 : 12} />
                {athletes
                  .filter(a => selectedAthletes.length === 0 || selectedAthletes.includes(a._id))
                  .flatMap((athlete, index) => {
                    const baseColor = getAthleteColor(athlete, index);
                    const athleteName = `${athlete.firstName} ${athlete.lastName}`;
                    return [
                      <Line
                        key={`${athlete._id}-avg`}
                        type="monotone"
                        dataKey={`${athleteName} (Avg)`}
                        stroke={baseColor}
                        strokeWidth={2}
                        name={`${athleteName} (Avg)`}
                        connectNulls={true}
                        strokeDasharray="5 5"
                      />,
                      <Line
                        key={`${athlete._id}-top`}
                        type="monotone"
                        dataKey={`${athleteName} (Top)`}
                        stroke={baseColor}
                        strokeWidth={2}
                        name={`${athleteName} (Top)`}
                        connectNulls={true}
                      />,
                      <Line
                        key={`${athlete._id}-worst`}
                        type="monotone"
                        dataKey={`${athleteName} (Worst)`}
                        stroke={baseColor}
                        strokeWidth={2}
                        name={`${athleteName} (Worst)`}
                        connectNulls={true}
                        strokeDasharray="3 3"
                      />,
                    ];
                  })}
              </LineChart>
            </ResponsiveContainer>
          </GraphContainer>
        ) : (
          <EmptyState>No combined distance data available</EmptyState>
        )}
      </GraphSection>

      {/* Average Distance and Best Distances Lists Side by Side - Hidden in jumper mode */}
      {!isJumperMode && (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
        {/* Average Distance List */}
        <GraphSection>
          <GraphTitle>Average Distance</GraphTitle>
          {startDate && endDate && selectedSessionTypes.length > 0 && avgDistanceBarData.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {avgDistanceBarData.map((item, index) => (
                <div
                  key={index}
                  style={{
                    padding: '0.75rem',
                    background: 'var(--bg-secondary)',
                    borderRadius: 'var(--radius-sm)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ fontFamily: 'Poppins', fontWeight: 600, color: 'var(--text-dark)' }}>
                    {item.athlete}
                  </span>
                  <span style={{ fontFamily: 'Poppins', fontWeight: 700, color: 'var(--accent-orange)' }}>
                    {item.distance}m
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState>No average distance data available</EmptyState>
          )}
        </GraphSection>

        {/* List of Best Distances by Athlete */}
        <GraphSection>
          <GraphTitle>Best Distances</GraphTitle>
          {startDate && endDate && selectedSessionTypes.length > 0 && topDistanceBarData.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {topDistanceBarData.map((item, index) => (
                <div
                  key={index}
                  style={{
                    padding: '0.75rem',
                    background: 'var(--bg-secondary)',
                    borderRadius: 'var(--radius-sm)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ fontFamily: 'Poppins', fontWeight: 600, color: 'var(--text-dark)' }}>
                    {item.athlete}
                  </span>
                  <span style={{ fontFamily: 'Poppins', fontWeight: 700, color: 'var(--accent-orange)' }}>
                    {item.distance}m
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState>No distance data available for selected athletes</EmptyState>
          )}
        </GraphSection>
      </div>
      )}

      {/* Average Distances and Best Distances by Location - Side by Side in jumper mode, separate in coach mode */}
      {isJumperMode ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
          {/* Average Distances by Location */}
          <GraphSection>
            <GraphTitle>Average Distances for Each Location</GraphTitle>
            {startDate && endDate && selectedSessionTypes.length > 0 && selectedAthletes.length > 0 && avgDistanceByLocation.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {avgDistanceByLocation.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '0.75rem',
                      background: 'var(--bg-secondary)',
                      borderRadius: 'var(--radius-sm)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ fontFamily: 'Poppins', fontWeight: 600, color: 'var(--text-dark)' }}>
                      {item.location}
                    </span>
                    <span style={{ fontFamily: 'Poppins', fontWeight: 700, color: 'var(--accent-orange)' }}>
                      {item.averageDistance}m
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState>No location distance data available</EmptyState>
            )}
          </GraphSection>

          {/* Best Distance for each Location */}
          <GraphSection>
            <GraphTitle>Best Distance for each Location</GraphTitle>
            {startDate && endDate && selectedSessionTypes.length > 0 && selectedAthletes.length > 0 && bestDistanceByLocation.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {bestDistanceByLocation.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '0.75rem',
                      background: 'var(--bg-secondary)',
                      borderRadius: 'var(--radius-sm)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ fontFamily: 'Poppins', fontWeight: 600, color: 'var(--text-dark)' }}>
                      {item.location}
                    </span>
                    <span style={{ fontFamily: 'Poppins', fontWeight: 700, color: 'var(--accent-orange)' }}>
                      {item.bestDistance}m
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState>No location distance data available</EmptyState>
            )}
          </GraphSection>
        </div>
      ) : (
        <GraphSection>
          <GraphTitle>Average Distances for Each Location</GraphTitle>
          {startDate && endDate && selectedSessionTypes.length > 0 && selectedAthletes.length > 0 && avgDistanceByLocation.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {avgDistanceByLocation.map((item, index) => (
                <div
                  key={index}
                  style={{
                    padding: '0.75rem',
                    background: 'var(--bg-secondary)',
                    borderRadius: 'var(--radius-sm)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ fontFamily: 'Poppins', fontWeight: 600, color: 'var(--text-dark)' }}>
                    {item.location}
                  </span>
                  <span style={{ fontFamily: 'Poppins', fontWeight: 700, color: 'var(--accent-orange)' }}>
                    {item.averageDistance}m
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState>No location distance data available</EmptyState>
          )}
        </GraphSection>
      )}
    </AnalyticsContainer>
  );
};

export default DistanceAnalytics;
